import { getWithHeaders, post } from "@selector/shared";
import { downloadFilescannerReport } from "@selector/shared/jsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { DialogComponent, formatDateTimeFromISO, Modal, TriggerButton, TriggerButtonIntent } from "widgets";
import { useOverlay } from "widgets/hooks";
import styleSkeleton from "widgets/skeleton.module.css";

import { FilePreviewTextField } from "@/modules/files/components/FilePreviewTextField.tsx";
import { notify } from "@/modules/notifications/notify.tsx";
import { FileScannerStatus, SubscriptionTypeEnum } from "@/modules/notifications/utils/types.ts";
import { webSocketClient } from "@/tools.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export const FileScannerCheckDialog = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { overlayProps, state } = useOverlay({
    isOpen,
    onOpenChange: (isOp: boolean) => {
      if (!isOp) {
        onClose();
      }
    },
  });

  const subscriptionIdRef = useRef<string>(null);
  const [filePreview, setFilePreview] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(true);
  const [lastModifiedDate, setLastModifiedDate] = useState<string>();

  async function fetchReport() {
    await getWithHeaders("/preview/filescannerReport", undefined, undefined, "text")
      .then(({ data, headers }) => {
        // Extract the last modified date
        const timestamp = headers.get("content-timestamp");
        if (data && timestamp) {
          setLastModifiedDate(timestamp);
          setFilePreview(data);
        } else {
          setFilePreview(undefined);
          setLastModifiedDate(undefined);
        }
      })
      .catch((error) => {
        notify.error({ title: "Failed to load report", children: error }, { autoClose: 5000 });
        setFilePreview(undefined);
        setLastModifiedDate(undefined);
      })
      .finally(() => {
        setPreviewLoading(false);
      });
  }

  const load = useCallback(() => {
    setPreviewLoading(true);
    void fetchReport();
  }, []);

  async function purge() {
    await post("/filescanner/deleteStaleFiles", undefined, undefined, undefined)
      .then(() => {
        notify.info(
          {
            title: "File System Purge",
            children: "This process has started and might take a moment.",
          },
          { autoClose: 5000 },
        );
      })
      .catch((error) => {
        notify.error({ title: "Purge failed", children: error }, { autoClose: 5000 });
        setFilePreview(undefined);
        setLastModifiedDate(undefined);
      });
  }

  async function refresh() {
    await post("/filescanner/refreshStaleFilesReport", undefined, undefined, undefined, "text")
      .then(() => {
        notify.info(
          {
            title: "File System Report",
            children: "Generation has started and might take a moment.",
          },
          { autoClose: 5000 },
        );
      })
      .catch((error) => {
        notify.error({ title: "Report generation failed", children: error }, { autoClose: 5000 });
      });
  }

  useEffect(() => {
    if (isOpen) {
      void fetchReport();
      subscriptionIdRef.current = webSocketClient.subscribe({
        subscription: { eventType: SubscriptionTypeEnum.FileScanner },
        onMessage(event) {
          if (
            event.status === FileScannerStatus.STALE_SCAN_SUCCESS ||
            event.status === FileScannerStatus.STALE_SCAN_UPDATED
          ) {
            load();
          }
        },
        onError: () => () => {
          notify.error({
            title: "An internal server error occurred",
          });
        },
      });
    } else {
      if (subscriptionIdRef.current) {
        webSocketClient.unsubscribe(subscriptionIdRef.current);
      }
      subscriptionIdRef.current = null;
    }
  }, [isOpen, load]);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title="Check File System Health"
        subtitle={
          previewLoading ? (
            <span className={styleSkeleton.skeleton}>Last modified: Jan 01, 1970, 00:00 AM</span>
          ) : lastModifiedDate ? (
            `Last modified: ${formatDateTimeFromISO(lastModifiedDate)}`
          ) : (
            ""
          )
        }
        data-testid="file-system-health"
        fullscreen
        actions={[
          <TriggerButton
            key={"report-download-button"}
            tooltip="Download the file system report"
            onPress={downloadFilescannerReport}
            icon={"download"}
            label={"Download"}
            isDisabled={filePreview == undefined}
            showLabel
          />,
          <TriggerButton
            key="refresh-button"
            tooltip="Generate a new file system report"
            onPress={refresh}
            icon="sync"
            label="Refresh"
            showLabel
          />,
          <TriggerButton
            key="purge-button"
            tooltip="Purge the file system"
            onPress={purge}
            icon="mop"
            label="Purge"
            isDisabled={filePreview == undefined}
            showLabel
            intent={TriggerButtonIntent.Danger}
          />,
        ]}
        closeFn={onClose}
      >
        <div style={{ height: "100%", display: "flex" }}>
          {filePreview == undefined && !previewLoading ? (
            <ContentPlaceholder>
              No report available.
              <br />
              Press the <b>Refresh</b> button if you want to generate one.
            </ContentPlaceholder>
          ) : (
            <FilePreviewTextField
              isLoading={previewLoading}
              key={`text-preview`}
              value={filePreview}
              style={{ borderWidth: "1px 0 0" }}
            />
          )}
        </div>
      </DialogComponent>
    </Modal>
  );
};
